import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/** The guest-only sheets mounted once in `App` (`GuestSheets`). */
export type GuestSheet = 'save' | 'signout';

interface UIState {
  /** Which guest sheet is open: "Save my account" or the sign-out warning. */
  guestSheet: GuestSheet | null;
  /** Optimistic locale while editing settings before save. */
  languagePreview: string | null;
  loading: boolean;
  error: string | null;
  success: string | null;
  modal: {
    isOpen: boolean;
    type: string | null;
    data: any;
  };
  scratchCard: {
    isRevealed: boolean;
    progress: number;
  };
}

const initialState: UIState = {
  guestSheet: null,
  languagePreview: null,
  loading: false,
  error: null,
  success: null,
  modal: {
    isOpen: false,
    type: null,
    data: null,
  },
  scratchCard: {
    isRevealed: false,
    progress: 0,
  },
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setLanguagePreview: (state, action: PayloadAction<string | null>) => {
      state.languagePreview = action.payload;
    },
    openGuestSheet: (state, action: PayloadAction<GuestSheet>) => {
      state.guestSheet = action.payload;
    },
    closeGuestSheet: state => {
      state.guestSheet = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setSuccess: (state, action: PayloadAction<string | null>) => {
      state.success = action.payload;
    },
    clearMessages: state => {
      state.error = null;
      state.success = null;
    },
    openModal: (state, action: PayloadAction<{ type: string; data?: any }>) => {
      state.modal.isOpen = true;
      state.modal.type = action.payload.type;
      state.modal.data = action.payload.data || null;
    },
    closeModal: state => {
      state.modal.isOpen = false;
      state.modal.type = null;
      state.modal.data = null;
    },
    setScratchCardRevealed: (state, action: PayloadAction<boolean>) => {
      state.scratchCard.isRevealed = action.payload;
    },
    setScratchCardProgress: (state, action: PayloadAction<number>) => {
      state.scratchCard.progress = action.payload;
    },
    resetScratchCard: state => {
      state.scratchCard.isRevealed = false;
      state.scratchCard.progress = 0;
    },
  },
});

export const {
  setLanguagePreview,
  openGuestSheet,
  closeGuestSheet,
  setLoading,
  setError,
  setSuccess,
  clearMessages,
  openModal,
  closeModal,
  setScratchCardRevealed,
  setScratchCardProgress,
  resetScratchCard,
} = uiSlice.actions;

export default uiSlice.reducer;
